import axios from "axios";
import { useCallback } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/popups";

const api = axios.create({
  baseURL: baseUrl,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("Popups API error:", error.response?.data || error.message);
    return Promise.reject(error);
  },
);

const usePopups = () => {
  const getAllPopups = useCallback(async () => {
    const response = await api.get("/");
    return response.data;
  }, []);

  const getPopupById = useCallback(async (id) => {
    const response = await api.get(`/${id}`);
    return response.data;
  }, []);

  const createPopup = useCallback(async (popupData) => {
    const response = await api.post("/", popupData);
    return response.data;
  }, []);

  const updatePopup = useCallback(async (id, popupData) => {
    const response = await api.put(`/${id}`, popupData);
    return response.data;
  }, []);

  const togglePopup = useCallback(async (id) => {
    const response = await api.patch(`/${id}/toggle`);
    return response.data;
  }, []);

  const deletePopup = useCallback(async (id) => {
    const response = await api.delete(`/${id}`);
    return response.data;
  }, []);

  // ===== Admin endpoints (manage popups for specific companies) =====
  const getPopupsByCompanyId = useCallback(async (companyId) => {
    const response = await api.get(`/company/${companyId}`);
    return response.data;
  }, []);

  const createPopupForCompany = useCallback(async (companyId, popupData) => {
    const response = await api.post(`/company/${companyId}`, popupData);
    return response.data;
  }, []);

  const updatePopupForCompany = useCallback(
    async (companyId, id, popupData) => {
      const response = await api.put(`/company/${companyId}/${id}`, popupData);
      return response.data;
    },
    [],
  );

  const togglePopupForCompany = useCallback(async (companyId, id) => {
    const response = await api.patch(`/company/${companyId}/${id}/toggle`);
    return response.data;
  }, []);

  const deletePopupForCompany = useCallback(async (companyId, id) => {
    const response = await api.delete(`/company/${companyId}/${id}`);
    return response.data;
  }, []);

  return {
    getAllPopups,
    getPopupById,
    createPopup,
    updatePopup,
    togglePopup,
    deletePopup,
    getPopupsByCompanyId,
    createPopupForCompany,
    updatePopupForCompany,
    togglePopupForCompany,
    deletePopupForCompany,
  };
};

export default usePopups;
